import api from '@/services/api';
import { isUuid } from '../utils/isUuid';

export const PRODUCT_NAMES_PER_PAGE = 100;
export const PRODUCT_NAMES_MAX_PAGES = 10;

const malformed = (detail: string): Error => new Error(`Malformed product names payload: ${detail}`);

const readEntry = (value: unknown): [string, string] => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) throw malformed('row is not an object');
    const { id, name } = value as Record<string, unknown>;
    if (typeof id !== 'string' || !isUuid(id)) throw malformed('id is not a valid id');
    if (typeof name !== 'string' || name === '') throw malformed('name is not a non-empty string');
    return [id.toLowerCase(), name];
};

const readPage = (body: unknown): { entries: [string, string][]; lastPage: number } => {
    if (typeof body !== 'object' || body === null) throw malformed('response is not an object');
    const { data, last_page: lastPage } = body as Record<string, unknown>;
    if (!Array.isArray(data)) throw malformed('data is not an array');
    if (typeof lastPage !== 'number' || !Number.isInteger(lastPage) || lastPage < 1) {
        throw malformed('last_page is not a positive integer');
    }
    return { entries: data.map(readEntry), lastPage };
};

/** Resolves false when the pass advertises more pages than the bound, so no partial set is kept. */
const collectPages = async (names: Map<string, string>, retired: boolean): Promise<boolean> => {
    let lastPage = 1;
    for (let page = 1; page <= lastPage; page += 1) {
        const response = await api.get('/smr/products', {
            params: {
                per_page: PRODUCT_NAMES_PER_PAGE,
                sort_by: 'name',
                page,
                ...(retired ? { show_retired: true } : {}),
            },
            suppressGlobalForbiddenToast: true,
        });
        const result = readPage(response.data);
        if (result.lastPage > PRODUCT_NAMES_MAX_PAGES) return false;
        lastPage = result.lastPage;
        result.entries.forEach(([id, name]) => names.set(id, name));
    }
    return true;
};

export const fetchProductNames = async (): Promise<Map<string, string>> => {
    const names = new Map<string, string>();
    if (!(await collectPages(names, false)) || !(await collectPages(names, true))) return new Map();
    return names;
};
